/* eslint-disable no-unused-vars */
/* eslint-disable no-undef */
import React, { useState, useId } from "react";

// * Package Imports
import axios from "axios";
import { toast } from "react-toastify";

// * Environment import
import { environment } from "../../environments/environment";

// * Import Context
import { useAuthContext } from "../../hooks/useAuthContext";

const CHECK_ICON = (
  <svg className="h-5 w-5 text-emerald-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

const CHEVRON_DOWN = (
  <svg className="h-5 w-5 shrink-0 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

function FaqAccordion({ title, items }) {
  const [openIndex, setOpenIndex] = useState(null);
  const idBase = useId();

  return (
    <div className="mt-12 sm:mt-16">
      <h2 className="text-lg font-semibold text-slate-900 mb-4">{title}</h2>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
        {items.map((item, index) => {
          const isOpen = openIndex === index;
          const triggerId = `${idBase}-trigger-${index}`;
          const panelId = `${idBase}-panel-${index}`;
          return (
            <div
              key={index}
              className={`border-b border-slate-200 last:border-b-0 ${isOpen ? "bg-slate-50/80" : ""}`}
            >
              <button
                type="button"
                id={triggerId}
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpenIndex(isOpen ? null : index)}
                className="w-full flex items-center justify-between gap-3 py-4 px-4 sm:px-5 text-left font-medium text-slate-900 hover:bg-slate-50/50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-emerald-500/30 transition-colors"
              >
                <span>{item.question}</span>
                <span
                  className={`text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                  aria-hidden
                >
                  {CHEVRON_DOWN}
                </span>
              </button>
              <div
                id={panelId}
                role="region"
                aria-labelledby={triggerId}
                className="grid transition-[grid-template-rows] duration-200 ease-out"
                style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
              >
                <div className="overflow-hidden">
                  <p className="pb-4 px-4 sm:px-5 pt-0 text-slate-600 text-sm leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const PricingPage = () => {
  document.title = `Pricing | LeadZLab`;

  const { user } = useAuthContext();
  const [loadingPlan, setLoadingPlan] = useState(null);

  const checkout = async (productId, planKey) => {
    setLoadingPlan(planKey);
    try {
      const { data } = await axios.post(
        `${environment.BACKEND}/user/payment/create-checkout-session`,
        { product_id: productId },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );
      if (!data?.url) {
        toast.error("Checkout URL not available. Please try again or contact support.", {
          position: "top-right",
          autoClose: true,
        });
        setLoadingPlan(null);
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      toast.error(`${err?.response?.data?.message ?? err.message}`, {
        position: "top-right",
        autoClose: true,
      });
      setLoadingPlan(null);
    }
  };

  const plans = [
    {
      key: "BASIC",
      name: "Basic",
      description: "For individuals and small projects",
      price: 25,
      interval: "month",
      productId: process.env.REACT_APP_BASIC_PLAN,
      features: [
        "Up to 3 scouts",
        "Up to 100 posts found per day",
        "Reddit & LinkedIn lead discovery",
        "Email support",
      ],
      cta: "Get started",
      highlighted: false,
    },
    {
      key: "ULTRA",
      name: "Ultra",
      description: "For serious lead gen and teams",
      price: 50,
      interval: "month",
      productId: process.env.REACT_APP_ULTRA_PLAN,
      features: [
        "Up to 10 scouts",
        "Up to 500 posts found per day",
        "Reddit & LinkedIn lead discovery",
        "Priority email support",
        "Best value for power users",
      ],
      cta: "Get started",
      highlighted: true,
      badge: "Most popular",
    },
  ];

  return (
    <div className="max-w-5xl mx-auto">
      {/* Hero */}
      <div className="text-center mb-10 sm:mb-12">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
          Simple pricing. More leads.
        </h1>
        <p className="mt-2 sm:mt-3 text-slate-600 text-base sm:text-lg max-w-xl mx-auto">
          Choose a plan and start finding qualified leads in minutes. Cancel anytime.
        </p>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-sm text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            3-day money-back guarantee
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            No long-term contract
          </span>
        </div>
        <p className="mt-4 text-xs text-slate-500 max-w-lg mx-auto">
          Post limits are daily maximum thresholds, not guaranteed volume. Actual posts found depend on scout settings and available content.
        </p>
      </div>

      {/* Plans */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
        {plans.map((plan) => (
          <div
            key={plan.key}
            className={`relative rounded-2xl border-2 bg-white p-6 sm:p-8 flex flex-col transition-shadow hover:shadow-lg ${
              plan.highlighted
                ? "border-emerald-500 shadow-md ring-2 ring-emerald-500/20"
                : "border-slate-200"
            }`}
          >
            {plan.badge && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="inline-flex items-center rounded-full bg-emerald-500 px-3 py-1 text-xs font-semibold text-white">
                  {plan.badge}
                </span>
              </div>
            )}
            <div className="mb-6">
              <h2 className="text-xl font-bold text-slate-900">{plan.name}</h2>
              <p className="mt-1 text-slate-600 text-sm">{plan.description}</p>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold tracking-tight text-slate-900">
                  ${plan.price}
                </span>
                <span className="text-slate-500">/{plan.interval}</span>
              </div>
            </div>
            <ul className="space-y-3 mb-8 flex-1">
              {plan.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700">
                  {CHECK_ICON}
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => checkout(plan.productId, plan.key)}
              disabled={!!loadingPlan}
              className={`w-full rounded-xl py-3.5 px-4 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                plan.highlighted
                  ? "bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-500"
                  : "bg-slate-900 text-white hover:bg-slate-800 focus:ring-slate-500"
              } disabled:opacity-60 disabled:cursor-not-allowed`}
            >
              {loadingPlan === plan.key ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Redirecting…
                </span>
              ) : (
                plan.cta
              )}
            </button>
          </div>
        ))}
      </div>

      {/* FAQ Accordion */}
      <FaqAccordion
        title="Frequently asked questions"
        items={[
          {
            question: "What does 'posts per day' mean?",
            answer:
              "It's the maximum number of leads (posts) your scouts can save per day. These are daily caps, not guaranteed volume—actual posts found depend on your scout settings, subreddits or search, and how much matching content exists.",
          },
          {
            question: "What is the refund policy?",
            answer:
              "We offer a 3-day money-back guarantee. If you're not satisfied, contact our support team within 3 days of your subscription purchase for a full refund.",
          },
          {
            question: "How do I upgrade my plan?",
            answer: "Contact support at support@leadzlab.co and we'll help you upgrade.",
          },
          {
            question: "Can I cancel anytime?",
            answer:
              "Yes. Cancel anytime—no long-term commitment. After you cancel, your subscription stays active until the end of the current billing period and you won't be charged again.",
          },
        ]}
      />
    </div>
  );
};

export default PricingPage;
